import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState, MatDialogConfig } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { captureException } from '@sentry/angular';
import { HomepageService } from 'app/services/homepage.service';
import { remove, uniqBy } from 'lodash';
import { BehaviorSubject, combineLatest, merge, NEVER, Observable, of, Subject, timer } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { MainHallPassFormComponent } from '../create-hallpass-forms/main-hallpass--form/main-hall-pass-form.component';
import { DeviceDetection } from '../device-detection.helper';
import { WaitingInLineEvents } from '../live-data/event-types';
import { HallPass, Location, NoFlyTime, PassLimit, Pinnable, Request, User, WaitingInLinePass, PassLimitInfo } from '../models';
import { WaitInLineCardComponent } from '../pass-cards/wait-in-line-card/wait-in-line-card.component';
import { RequestCardComponent } from '../request-card/request-card.component';
import { FeatureFlagService, FLAGS } from '../services/feature-flag.service';
import { HallPassesService, MODAL_MAX_HEIGHT } from '../services/hall-passes.service';
import { HttpService } from '../services/http-service';
import { KioskModeService } from '../services/kiosk-mode.service';
import { LiveUpdateEvent, LiveUpdateService } from '../services/live-update.service';
import { LocationsService } from '../services/locations.service';
import { NoFlyTimeService } from '../services/no-fly-time.service';
import { PassLimitService } from '../services/pass-limit.service';
import { StorageKeys, StorageService } from '../services/storage.service';
import { ToastService } from '../services/toast.service';
import { UserService } from '../services/user.service';
import { sortWil, WaitInLineService } from '../services/wait-in-line.service';
import { NoFlyTimeDialogComponent } from '../shared/shared-components/no-fly-time-dialog/no-fly-time-dialog.component';

declare const window: Window & { appLoaded?: (delay: number) => void };

@Component({
	selector: 'app-kiosk-mode',
	templateUrl: './kiosk-mode.component.html',
	styleUrls: ['./kiosk-mode.component.scss'],
})
export class KioskModeComponent implements OnInit, AfterViewInit, OnDestroy {
	_activePassCollection: BehaviorSubject<HallPass[]> = new BehaviorSubject<HallPass[]>([]);
	activeHallPasses$: Observable<HallPass[]>;

	_wilPassCollection: BehaviorSubject<WaitingInLinePass[]> = new BehaviorSubject<WaitingInLinePass[]>([]);
	waitingInLinePasses$: Observable<WaitingInLinePass[]>;
	hoveredWilPassId: number | null = null;
	passLimits: { [id: number]: PassLimit } = {};
	wilBlocked$: Observable<boolean> = of(false);

	private locationAvailable$: Observable<Location>;

	private destroy$: Subject<void> = new Subject<void>();
	currentLocation: Location;
	private showAsOriginRoom = true;
	private isSmartphone = DeviceDetection.isAndroid() || DeviceDetection.isIOSMobile();

	showProfilePicture = false;
	waitInLinePassesEnabled = false;
	isWILRefreshEnabled = false;
	isOriginLineFFEnabled = false;

	noFlyTimes: NoFlyTime[] = [];
	currentNoFlyTimeIndex: number;
	currentNoFlyEndTimeString: string;
	noFlyTimeActive = false;
	noFlyTimeTooltipDialogRef: MatDialogRef<NoFlyTimeDialogComponent>;

	inputBlurString: string;
	showError = false;
	inputFocused = true;

	mainFormRef: MatDialogRef<MainHallPassFormComponent>;
	frontOfWaitInLineDialogRef: MatDialogRef<WaitInLineCardComponent>;
	alreadyOpenedWil: Record<string, boolean> = {};

	waitInLineTitle: Observable<string> = timer(0, 750).pipe(
		takeUntil(this.destroy$),
		map((count) => `Waiting in Line${'.'.repeat(count % 4)}`)
	);

	enableIdSearch: boolean;
	enableIdScanning: boolean;
	enableNameOrEmailSearch = true;

	constructor(
		private dialog: MatDialog,
		public kioskMode: KioskModeService,
		private locationService: LocationsService,
		private userService: UserService,
		private passesService: HallPassesService,
		private wilService: WaitInLineService,
		private titleService: Title,
		private featureService: FeatureFlagService,
		private toast: ToastService,
		private studentPassLimitService: PassLimitService,
		private http: HttpService,
		private liveUpdateService: LiveUpdateService,
		private noFlyTimeService: NoFlyTimeService,
		public homePageService: HomepageService,
		private storageService: StorageService
	) {}

	ngOnInit(): void {
		const needsToRefresh = this.storageService.getItem(StorageKeys.kioskModeForceRefresh);
		if (needsToRefresh !== null) {
			// this implementation ensures that if the user refreshes the page after the hard reload, they won't end up
			// in a refresh loop
			this.storageService.removeItem(StorageKeys.kioskModeForceRefresh);
			window.location.reload();
		}

		// POPULATE KIOSK MODE PROPERTIES
		this.kioskMode.triggerFetchFromStorage();
		const settings = this.kioskMode.getKioskModeSettings();
		this.enableIdScanning = settings.findByScan;
		this.enableNameOrEmailSearch = settings.findByName;
		this.enableIdSearch = settings.findById;
		this.waitInLinePassesEnabled = this.featureService.isFeatureEnabled(FLAGS.WaitInLine);
		this.isWILRefreshEnabled = this.featureService.isFeatureEnabledV2(FLAGS.WaitInLineUIRefresh);

		combineLatest([this.noFlyTimeService.noFlyTimes$, this.noFlyTimeService.noFlyTimeEnabled])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([noFlyTimes, noFlyTimeEnabled]) => {
				this.noFlyTimes = noFlyTimes;
				this.currentNoFlyTimeIndex = this.noFlyTimeService.checkNoFlyTimes(this.noFlyTimes);
				if (this.currentNoFlyTimeIndex >= 0 && noFlyTimeEnabled) {
					this.noFlyTimeActive = true;
					const currentEndTime = this.noFlyTimeService.momentize(this.noFlyTimes[this.currentNoFlyTimeIndex].end_time);
					this.currentNoFlyEndTimeString = this.noFlyTimeService.formatTimeString(currentEndTime);
				} else {
					this.noFlyTimeActive = false;
				}
			});

		// show/hide profile pictures
		if (this.userService.getUserSchool()?.profile_pictures_enabled) {
			// not a long-lived listener since we take once (and automatically unsubscribes)
			this.userService.user$.pipe(take(1)).subscribe({
				next: (user) => {
					this.showProfilePicture = user?.show_profile_pictures === 'everywhere' || user?.show_profile_pictures === 'hall_monitor';
				},
			});
		} else {
			this.showProfilePicture = false;
		}

		/**
		 * Observables that depend on the location to be available should start off their RxJS chain
		 * using locationAvailable$. This ensures that the kiosk location is defined before executing
		 * code that requires the location.
		 * This makes all dependant Observables a little safer
		 */
		this.locationAvailable$ = this.kioskMode.currentRoom$.pipe(
			filter<Location>(Boolean),
			tap((currentRoom) => {
				this.currentLocation = currentRoom;
				this.wilBlocked$ = this.wilService.watchWILBlockedStatus(this.currentLocation.id);
				this.titleService.setTitle(`${this.currentLocation.title} | SmartPass`);
			})
		);
		this.locationService.pass_limits_entities$.pipe(takeUntil(this.destroy$)).subscribe((res) => {
			this.passLimits = res;
		});

		// Prepare long-lived listeners

		this.toast.toastButtonClick$
			.pipe(
				takeUntil(this.destroy$),
				filter((action) => action === 'update_show_as_origin')
			)
			.subscribe((res) => {
				// opens "learn more" link in show as origin error toast
				window.open('https://www.smartpass.app/show-as-origin-room', '_blank');
			});

		this.activeHallPasses$ = this._activePassCollection.asObservable().pipe(
			map((hps) => {
				return hps.sort((hp1, hp2) => {
					return new Date(hp1.created).getTime() - new Date(hp2.created).getTime();
				});
			}),
			catchError((error, originalObs) => {
				captureException(error, {
					extra: {
						message: 'Error on Kiosk Mode: Active Hall Pass Chain',
					},
				});
				return originalObs;
			})
		);

		this.waitingInLinePasses$ = this._wilPassCollection.asObservable().pipe(
			map((wilPasses) => wilPasses.sort(sortWil)),
			takeUntil(this.destroy$)
		);

		this.popUpReadyToStartWil();
		this.listenToOriginRoomChanges();

		combineLatest([this.locationAvailable$, this.liveUpdateService.isConnected$])
			.pipe(
				takeUntil(this.destroy$),
				filter(([currentRoom, isConnected]) => !!currentRoom && isConnected),
				switchMap(([currentRoom, _]) => merge(this.listenToActivePasses(currentRoom), this.listenToWaitingInLinePasses(currentRoom)))
			)
			.subscribe({
				error: (err) => {
					// final place where an error can be caught, but not handled
					captureException(err, {
						extra: {
							message: `Kiosk mode Observable died due to: ${err.message}`,
							originalError: err,
						},
					});
				},
			});
	}

	/**
	 * This handles the edge case where an admin edits this room to no longer show by location
	 * after a user has already logged into this kiosk location
	 */
	private listenToOriginRoomChanges() {
		this.locationAvailable$
			.pipe(
				switchMap((location) => {
					return this.passesService.getPinnables().pipe(
						map((pins) => {
							const pin = pins.find((p) => {
								if (location.category) {
									return p.category === location.category;
								}

								return p.location?.id === location.id;
							});

							this.showAsOriginRoom = pin.show_as_origin_room;
							return pin;
						})
					);
				}),
				switchMap((pin) =>
					this.locationService.listenPinnableSocket().pipe(
						map<LiveUpdateEvent, Pinnable>((pinEvent) => pinEvent.data as Pinnable),
						filter((incomingPin) => incomingPin.id === pin.id),
						tap((incomingPin) => {
							this.showAsOriginRoom = incomingPin.show_as_origin_room;
						})
					)
				),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	private listenToActivePasses(currentRoom: Location): Observable<never> {
		return this.passesService.getActivePassesKioskMode(currentRoom.id).pipe(
			map((hps) => hps.map(HallPass.fromJSON)),
			catchError((error) => {
				captureException(error, {
					extra: {
						message: 'Failed to get active passes in kiosk mode',
					},
				});
				return of(this._activePassCollection.value);
			}),
			switchMap((currentHallPasses) => {
				this._activePassCollection.next(currentHallPasses);
				// set values

				const hallPassCreatedListener$ = merge(
					this.liveUpdateService.listen('hall_pass.start'),
					this.liveUpdateService.listen('pass_request.accept')
				).pipe(
					map((wsEvent) => HallPass.fromJSON(wsEvent.data)),
					filter((hp) => {
						const { origin, destination } = hp;
						return origin.id === this.currentLocation.id || destination.id === this.currentLocation.id;
					}),
					tap((hp) => {
						const newValues = uniqBy(this._activePassCollection.value.concat(hp), (pass) => pass.id);
						this._activePassCollection.next(newValues); // comment this out to simulate kiosk-monitoring-service firing a sentry report
						this.closeDialogIfRequestAccepted(newValues);
					}),
					takeUntil(this.destroy$)
				);

				const hallPassDeletedListener$ = this.liveUpdateService.listen('hall_pass.end').pipe(
					map((wsEvent) => HallPass.fromJSON(wsEvent.data)),
					filter((hp) => hp.origin.id === this.currentLocation.id || hp.destination.id === this.currentLocation.id),
					tap((hp) => {
						const passes = this._activePassCollection.value;
						remove(passes, (hallPass) => hallPass.id === hp.id);
						this._activePassCollection.next(passes);
					})
				);

				return merge(hallPassCreatedListener$, hallPassDeletedListener$).pipe(switchMap(() => NEVER));
			}),
			takeUntil(this.destroy$)
		);
	}

	private listenToWaitingInLinePasses(currentRoom: Location): Observable<never> {
		if (!this.featureService.isFeatureEnabled(FLAGS.WaitInLine)) {
			return NEVER;
		}

		return this.wilService.fetchWaitingInLinePasses({ origin_id: currentRoom.id }).pipe(
			catchError((error, _) => {
				captureException(error, {
					extra: {
						message: 'Failed to get waiting in line passes in kiosk mode',
					},
				});
				return of(this._wilPassCollection.value);
			}),
			switchMap((wilPasses) => {
				this._wilPassCollection.next(wilPasses);
				const parseAndFilter = (source: Observable<LiveUpdateEvent>) =>
					source.pipe(
						map((wsEvent) => {
							const data = Array.isArray(wsEvent.data) ? wsEvent.data[0] : wsEvent.data;

							return WaitingInLinePass.fromJSON(data);
						}),
						filter((wilPass) => {
							const mainFilter = wilPass?.origin.id === this.currentLocation.id;

							if (this.isOriginLineFFEnabled && this.currentLocation.max_passes_from_active) {
								return mainFilter && wilPass.is_origin_line;
							}

							return mainFilter;
						}),
						takeUntil(this.destroy$)
					);

				const wilPassCreatedListener$ = this.liveUpdateService.listen(WaitingInLineEvents.Create).pipe(
					parseAndFilter,
					tap((wilPass) => {
						this._wilPassCollection.next([...this._wilPassCollection.value, wilPass]);
					})
				);

				const wilPassUpdatedListener$ = this.liveUpdateService.listen(WaitingInLineEvents.Update).pipe(
					parseAndFilter,
					tap((wilPass) => {
						const collection = this._wilPassCollection.value;
						remove(collection, (pass) => pass.id === wilPass.id);
						this._wilPassCollection.next([...collection, wilPass]);
					})
				);

				const wilPassDeletedListener$ = this.liveUpdateService.listen(WaitingInLineEvents.Delete).pipe(
					parseAndFilter,
					tap((wilPass) => {
						const collection = this._wilPassCollection.value;
						remove(collection, (pass) => pass.id === wilPass.id);
						this._wilPassCollection.next(collection);
					})
				);

				return merge(wilPassCreatedListener$, wilPassUpdatedListener$, wilPassDeletedListener$).pipe(switchMap(() => NEVER));
			}),
			switchMap(() => NEVER)
		);
	}

	/**
	 * Controls automatically popping up a Ready-to-Start Waiting-in-Line pass
	 * on the kiosk.
	 */
	private popUpReadyToStartWil() {
		// This code is very similar to the same-named function in the home-page service.
		// There are some parameters and conditions here that are different and would
		// be slightly difficult to refactor into a single component.
		this.waitingInLinePasses$
			.pipe(
				takeUntil(this.destroy$),
				filter((wilPasses) => {
					return (
						wilPasses.length > 0 &&
						wilPasses[0].isReadyToStart() &&
						this.frontOfWaitInLineDialogRef?.getState() !== MatDialogState.OPEN &&
						!(wilPasses[0].id in this.alreadyOpenedWil) &&
						!!wilPasses[0].start_attempt_end_time
					);
				}),
				catchError((error, originalObs) => {
					captureException(error, {
						extra: {
							message: 'Error on Kiosk Mode: Waiting in Line Passes Chain',
						},
					});
					return originalObs;
				})
			)
			.subscribe({
				next: (passes: WaitingInLinePass[]) => {
					const readyToStartPass = passes[0];
					this.alreadyOpenedWil[readyToStartPass.id] = true;
					const modalHeight = this.passesService.getModalHeight(readyToStartPass, true, this.kioskMode.isKioskMode());
					const modalWidth = this.passesService.getModalWidth(false);
					const modalMinWidth = this.passesService.getModalMinWidth(false);
					const config: MatDialogConfig = {
						panelClass: 'teacher-pass-card-dialog-container',
						backdropClass: 'custom-backdrop',
						width: modalWidth,
						height: modalHeight,
						minWidth: modalMinWidth,
						maxWidth: modalMinWidth,
						disableClose: false,
						maxHeight: `${MODAL_MAX_HEIGHT}px`,
						closeOnNavigation: true,
						data: {
							nextInLine: true,
							pass: readyToStartPass,
							forStaff: false,
							forKiosk: true,
						},
					};
					this.frontOfWaitInLineDialogRef = this.dialog.open(WaitInLineCardComponent, config);
				},
			});
	}

	/**
	 * The following listener is responsible for checking if incoming hall passes are the result
	 * of an accepted pass requests.
	 *
	 * If there are more than one hall pass, and the dialog is currently opened, the pinnable key
	 * inside MainHallPassFormComponent.FORM_STATE will represent a pass request.
	 * If the origin id, destination id and student id are the same then the incoming hall pass was
	 * created from the pass request in the dialog, and therefore we can close the dialog
	 */
	private closeDialogIfRequestAccepted(passes: HallPass[]) {
		if (passes.length > 0 && this.mainFormRef?.getState() === MatDialogState.OPEN) {
			const formState = this.mainFormRef?.componentInstance?.FORM_STATE;
			if (formState?.data.direction?.pinnable && formState?.data.kioskModeStudent) {
				const requestMatch = passes.filter(
					(p) =>
						p.origin.id == formState.data.direction.from.id &&
						p.destination.id == formState.data.direction.to.id &&
						p.student.id == formState.data.kioskModeStudent.id
				);
				if (requestMatch.length > 0) {
					this.mainFormRef.close();
				}
			}
		}
	}

	ngAfterViewInit(): void {
		if (window?.appLoaded) {
			window.appLoaded(1000);
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	async showMainForm(student: User) {
		// don't allow creating a pass if the room does not have show as origin room setting set to true.
		if (!this.showAsOriginRoom) {
			this.toast.openToast({
				title: 'Passes cannot be created on this kiosk',
				subtitle: 'This room is only allowed as a destination, and therefore cannot become a kiosk.',
				showButton: true,
				action: 'update_show_as_origin',
				buttonText: 'Learn more.',
				type: 'error',
			});
			return;
		}

		this.studentPassLimitService.setPassLimitUser(student);
		if (this.kioskMode.getCurrentRoom().value) {
			this.studentPassLimitService.passLimitStudent$
				.asObservable()
				.pipe(
					takeUntil(this.destroy$),
					take(1),
					filter((student) => !!student),
					switchMap(() => {
						return this.studentPassLimitService.passLimitChanges$().pipe(take(1));
					}),
					catchError((error) => {
						captureException(error, {
							extra: {
								message: `Error on Kiosk Mode: Pass Limit Chain for schoolId: ${this.http.getSchool().id}`,
							},
						});
						return of(null);
					})
				)
				.subscribe((pl) => {
					this.openMainFormDialog(student, this.kioskMode.getCurrentRoom().value, pl);
				});
			return;
		}

		this.locationAvailable$
			.pipe(
				switchMap((currentRoom) => {
					this.currentLocation = currentRoom;
					return this.studentPassLimitService.passLimitChanges$();
				}),
				takeUntil(this.destroy$)
			)
			.subscribe((passLimitInfo: PassLimitInfo) => {
				this.openMainFormDialog(student, this.currentLocation, passLimitInfo);
			});

		this.kioskMode.triggerFetchFromStorage();
	}

	openNoFlyTimeTooltip() {
		if (!this.noFlyTimeActive) {
			return;
		}
		this.noFlyTimeTooltipDialogRef = this.dialog.open(NoFlyTimeDialogComponent, {
			data: {
				noFlyTimes: this.noFlyTimes,
				currentNoFlyTimeIndex: this.currentNoFlyTimeIndex,
				closeDialogRef: this.closeNoFlyTimeTooltip.bind(this),
			},
			panelClass: 'no-fly-time-tooltip-dialog-container',
		});
	}

	closeNoFlyTimeTooltip() {
		this.noFlyTimeTooltipDialogRef?.close();
	}

	private openMainFormDialog(student: User, kioskModeRoom: Location, passLimitInfo: PassLimitInfo): void {
		this.mainFormRef = this.dialog.open(MainHallPassFormComponent, {
			panelClass: 'main-form-dialog-container',
			maxWidth: '686px',
			maxHeight: '988px',
			backdropClass: 'custom-backdrop',
			data: {
				forLater: false,
				forStaff: true,
				forInput: true,
				lockOriginLocation: true,
				kioskMode: true,
				passLimitInfo,
				kioskModeRoom: kioskModeRoom,
				kioskModeSelectedUser: [student],
				noFlyTimes: this.noFlyTimes,
				currentNoFlyTimeIndex: this.currentNoFlyTimeIndex,
				noFlyTimeActive: this.noFlyTimeActive,
				currentNoFlyEndTimeString: this.currentNoFlyEndTimeString,
			},
		});

		this.mainFormRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((results) => {
				this.studentPassLimitService.setPassLimitUser(null);
				if (results instanceof Request) {
					const data = {
						pass: results,
						fromPast: false,
						isActive: true,
						forFuture: false,
						hasDeleteButton: false,
						forMonitor: false,
						forStaff: false,
						kioskMode: true,
						hideReport: true,
						activePassTime$: null,
						showStudentInfoBlock: this.kioskMode.getCurrentRoom().value,
					};
					const modalHeight = this.passesService.getModalHeight(results, true, true);
					const modalWidth = this.passesService.getModalWidth(this.isSmartphone);
					const modalMinWidth = this.passesService.getModalMinWidth(this.isSmartphone);
					this.dialog.open(RequestCardComponent, {
						panelClass: 'teacher-pass-card-dialog-container',
						backdropClass: 'custom-backdrop',
						width: modalWidth,
						height: modalHeight,
						minWidth: modalMinWidth,
						maxWidth: '686px',
						maxHeight: `${MODAL_MAX_HEIGHT}px`,
						data: data,
					});
				}
			});
	}

	getPlaceholder(): string {
		if (this.enableIdSearch && this.enableNameOrEmailSearch) {
			return 'No student found for';
		} else if (this.enableIdSearch || this.enableIdScanning) {
			return 'No student found for ID Number';
		} else {
			return 'No student found for';
		}
	}

	blurEvent(event: { showError: boolean; string: string }): void {
		this.inputFocused = false;
		this.showError = event.showError;
		this.inputBlurString = event.string;
	}

	focusEvent(): void {
		this.inputFocused = true;
	}
}
